import React, { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Form, Input, Modal, Typography, message } from 'antd';
import { KeyOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import type { RuleObject } from 'antd/es/form';
import type { StoreValue } from 'antd/es/form/interface';
import { useStore } from '../../../store/useStore';
import { resolveAccount } from '../../../utils/identity';
import useCountdown from '../../../hooks/useCountDown';
import { useTranslation } from '../../../i18n';
import {
  containsPasswordWhitespace,
  utf8ByteLength
} from '../userCenter/userCenterData';

type ResetPasswordFormValues = {
  account?: string;
  verifyCode?: string;
  newPassword?: string;
  confirmPassword?: string;
};

type ResetPasswordModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const { Text } = Typography;

export function ResetPasswordModal({ open, onClose, onSuccess }: ResetPasswordModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm<ResetPasswordFormValues>();
  const { loginStore } = useStore();
  const { countdown, isRunning, startCountdown, resetCountdown } = useCountdown();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    resetCountdown();
    // 仅在打开时重置；resetCountdown 每渲染新建，勿放入依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const accountFieldRules = useMemo(
    () => [
      {
        validator: (_rule: RuleObject, value: StoreValue) => {
          const text = typeof value === 'string' ? value.trim() : '';
          if (!text) return Promise.reject(new Error(t('login.account_required')));
          if (resolveAccount(text)) return Promise.resolve();
          return Promise.reject(new Error(t('login.account_invalid')));
        }
      }
    ],
    [t]
  );

  const sendVerifyCode = async () => {
    if (isRunning) return;
    const values = await form.validateFields(['account']).catch(() => null);
    const credential = resolveAccount(String(values?.account ?? ''));
    if (!credential) return;
    const identity = 'phone' in credential ? credential.phone : credential.email;
    try {
      const res = await loginStore.sendVerificationCode(identity, 'RESET_PASSWORD');
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        startCountdown();
      } else {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    }
  };

  const submit = async (values: ResetPasswordFormValues) => {
    if (submitting) return;
    const credential = resolveAccount(values.account?.trim() ?? '');
    if (!credential) return;
    const identity = 'phone' in credential ? credential.phone : credential.email;
    setSubmitting(true);
    try {
      const res = await loginStore.resetPassword({
        identity,
        code: values.verifyCode ?? '',
        newPassword: values.newPassword ?? ''
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('login.reset_password_failed'));
        return;
      }
      message.success(res.message || t('login.reset_password_success'));
      form.resetFields();
      resetCountdown();
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('login.reset_password_failed_retry'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('login.reset_password_title')}
      onCancel={onClose}
      destroyOnClose
      centered
      width={420}
      footer={null}
      className='login-reset-modal'
    >
      <Form<ResetPasswordFormValues>
        form={form}
        layout='vertical'
        requiredMark={false}
        onFinish={submit}
        className='login-reset-form'
      >
        <Form.Item
          name='account'
          label={<Text className='login-panel-field-label'>{t('login.account_label')}</Text>}
          rules={accountFieldRules}
        >
          <Input
            prefix={<UserOutlined className='site-form-item-icon' />}
            placeholder={t('login.account_placeholder')}
            size='large'
            maxLength={320}
          />
        </Form.Item>

        <Form.Item label={<Text className='login-panel-field-label'>{t('login.code_label')}</Text>}>
          <Flex gap={8} align='start'>
            <Form.Item
              name='verifyCode'
              className='login-panel-verify-item'
              style={{ flex: 1, marginBottom: 0 }}
              rules={[
                { required: true, message: t('login.code_required') },
                { pattern: /^[0-9]{6}$/, message: t('login.code_digits') }
              ]}
            >
              <Input
                prefix={<KeyOutlined className='site-form-item-icon' />}
                placeholder={t('login.code_placeholder')}
                size='large'
                maxLength={6}
              />
            </Form.Item>
            <Button size='large' className='login-panel-send-code' disabled={isRunning} onClick={sendVerifyCode}>
              {isRunning ? `${countdown}s` : t('login.send_code')}
            </Button>
          </Flex>
        </Form.Item>

        <Form.Item
          name='newPassword'
          label={<Text className='login-panel-field-label'>{t('login.new_password_label')}</Text>}
          rules={[
            { required: true, message: t('login.new_password_required') },
            {
              validator: (_rule, value: string | undefined) => {
                const password = value ?? '';
                if (!password) return Promise.resolve();
                const bytes = utf8ByteLength(password);
                if (
                  password.length >= 12 &&
                  password.length <= 72 &&
                  bytes <= 72 &&
                  !containsPasswordWhitespace(password)
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('login.reset_password_rule')));
              }
            }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className='site-form-item-icon' />}
            placeholder={t('login.reset_password_rule')}
            size='large'
            maxLength={72}
          />
        </Form.Item>

        <Form.Item
          name='confirmPassword'
          label={<Text className='login-panel-field-label'>{t('login.confirm_password_label')}</Text>}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('login.confirm_password_required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error(t('login.password_mismatch')));
              }
            })
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className='site-form-item-icon' />}
            placeholder={t('login.confirm_password_required')}
            size='large'
            maxLength={72}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
          <Button type='primary' htmlType='submit' block size='large' loading={submitting}>
            {t('login.reset_password_btn')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
